import { AuthToken } from "@/types/auth";
import { explorerPrefixedPath } from "@/utils";

export type WorkOSUser = {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  profilePictureUrl?: string;
  metadata?: Record<string, unknown>;
  createdAt?: string;
};

export type AuthUserProfile<T> = {
  sub?: string;
  user_id?: string;
  name?: string;
  email?: string;
  family_name?: string;
  given_name?: string;
  picture?: string;
  created_at?: string;
  user_metadata: T;
};

export type WorkOSAuthResponse = {
  accessToken: string;
  user: WorkOSUser;
  is_new_user?: boolean;
};

export type WorkOSEmailVerificationRequired = {
  requiresEmailVerification: true;
  email: string;
  pending_authentication_token: string;
  email_verification_id?: string;
};

const PROVIDER_MAP: Record<
  string,
  "GoogleOAuth" | "GitHubOAuth" | "AppleOAuth" | "MicrosoftOAuth"
> = {
  "google-oauth2": "GoogleOAuth",
  google: "GoogleOAuth",
  GoogleOAuth: "GoogleOAuth",
  github: "GitHubOAuth",
  GitHubOAuth: "GitHubOAuth",
  apple: "AppleOAuth"
};

export const BASE_URL = "";

export const getProviderValue = (value?: string) => {
  if (!value) {
    throw new Error("provider not found");
  }
  const mapped = PROVIDER_MAP[value];
  if (!mapped) {
    throw new Error(`unsupported provider: ${value}`);
  }
  return mapped;
};

export const mapWorkOSUserToProfile = <T>(user: WorkOSUser): AuthUserProfile<T> => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");

  return {
    sub: user.id,
    user_id: user.id,
    email: user.email,
    name: name || user.email,
    given_name: user.firstName,
    family_name: user.lastName,
    picture: user.profilePictureUrl,
    created_at: user.createdAt,
    user_metadata: (user.metadata || {}) as T
  };
};

export const getAuthorizationUrl = async (provider: string, screenHint?: "sign-up" | "sign-in") => {
  const params = new URLSearchParams({
    provider: getProviderValue(provider),
    loginPath: explorerPrefixedPath("/login")
  });
  if (screenHint) {
    params.set("screenHint", screenHint);
  }

  const response = await fetch(
    `${BASE_URL}/api/workos/authorize?${params.toString()}&origin=${window.location.origin}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include"
    }
  );

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Unable to start WorkOS login");
  }

  return data.url as string;
};

export const exchangeCodeForSession = async (
  code: string,
  state: string
): Promise<WorkOSAuthResponse | WorkOSEmailVerificationRequired> => {
  const response = await fetch(`${BASE_URL}/api/workos/callback`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    credentials: "include",
    body: JSON.stringify({ code, state })
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Unable to finish WorkOS login");
  }

  // Check if email verification is required
  if (data.requiresEmailVerification === true) {
    return data as WorkOSEmailVerificationRequired;
  }

  return data as WorkOSAuthResponse;
};

export const verifyEmailVerificationCode = async (
  code: string,
  pendingAuthenticationToken: string,
  emailVerificationId?: string
): Promise<WorkOSAuthResponse> => {
  const response = await fetch(`${BASE_URL}/api/workos/verify-email`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    credentials: "include",
    body: JSON.stringify({
      code,
      pending_authentication_token: pendingAuthenticationToken,
      email_verification_id: emailVerificationId
    })
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Unable to verify email");
  }

  return data as WorkOSAuthResponse;
};

export const resendEmailVerificationCode = async (
  email: string,
  pendingAuthenticationToken: string,
  emailVerificationId?: string
): Promise<void> => {
  const response = await fetch(`${BASE_URL}/api/workos/resend-email-verification`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    credentials: "include",
    body: JSON.stringify({
      email,
      pending_authentication_token: pendingAuthenticationToken,
      ...(emailVerificationId && { email_verification_id: emailVerificationId })
    })
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Unable to resend verification email");
  }
};

export const refreshSession = async (): Promise<WorkOSAuthResponse> => {
  const response = await fetch(`${BASE_URL}/api/workos/refresh`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    credentials: "include"
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Unable to refresh session");
  }

  return data as WorkOSAuthResponse;
};

export const logoutSession = async (accessToken?: string, returnTo?: string) => {
  const response = await fetch(`${BASE_URL}/api/workos/logout`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
    },
    credentials: "include",
    body: JSON.stringify({ returnTo })
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Unable to logout");
  }

  return data.url as string;
};

export const decodeToken = <T extends AuthToken>(result: string) => {
  try {
    const base64Url = result.split(".")[1];
    const base64 = base64Url
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(base64Url.length + ((4 - (base64Url.length % 4)) % 4), "=");

    const json = atob(base64);
    return JSON.parse(json) as T;
  } catch (e) {
    console.error("Failed to decode token", e);
    return undefined;
  }
};

export const getUserAttribute = <T>(profile: AuthUserProfile<T> | undefined, attribute: string) => {
  if (!profile) {
    return;
  }
  if (profile.user_metadata[attribute as keyof typeof profile.user_metadata]) {
    return profile.user_metadata[attribute as keyof typeof profile.user_metadata];
  }
  return profile[attribute as keyof typeof profile];
};

export const getUserName = (profile?: AuthUserProfile<unknown>) => {
  return getUserAttribute(profile, "name");
};

export const patchUserMetadata = async <T>(
  token: string | undefined,
  userId: string | undefined,
  meta: Partial<T> | null
): Promise<void> => {
  if (!token) {
    throw new Error("token not found");
  }
  if (!userId) {
    throw new Error("userId not found");
  }

  const response = await fetch(`${BASE_URL}/api/update-metadata`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      userId,
      metadata: meta
    })
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Metadata update failed");
  }
};

export const getUser = async <T>(token: string, userId: string): Promise<AuthUserProfile<T>> => {
  const response = await fetch(`${BASE_URL}/api/get-metadata`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      userId
    })
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || "Metadata get failed");
  }

  return mapWorkOSUserToProfile<T>(data.user as WorkOSUser);
};
